import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Tag, Percent, DollarSign, Calendar, Users, 
  Trash2, Edit, ToggleLeft, ToggleRight, Copy, CheckCircle, 
  XCircle, Search, Loader2, Sparkles, Download
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminPromoCodes = () => {
  const navigate = useNavigate();
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);

  // Form state for create/edit
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: 10,
    start_date: '',
    end_date: '',
    max_uses: '',
    max_uses_per_customer: 1,
    min_order_amount: '',
    max_discount_amount: '',
    is_active: true
  });

  // Generate form state
  const [generateData, setGenerateData] = useState({
    prefix: 'PROMO',
    count: 5,
    discount_type: 'percentage',
    discount_value: 10,
    end_date: '',
    max_uses: 1,
    min_order_amount: ''
  });

  useEffect(() => {
    fetchPromoCodes();
  }, []);

  const fetchPromoCodes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/promo-codes/admin/list`);
      setPromoCodes(response.data);
    } catch (err) {
      setError('Erreur lors du chargement des codes promo');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        discount_value: parseFloat(formData.discount_value),
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        max_uses_per_customer: formData.max_uses_per_customer ? parseInt(formData.max_uses_per_customer) : null,
        min_order_amount: formData.min_order_amount ? parseFloat(formData.min_order_amount) : null,
        max_discount_amount: formData.max_discount_amount ? parseFloat(formData.max_discount_amount) : null,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null
      };

      if (editingCode) {
        await axios.put(`${BACKEND_URL}/api/promo-codes/admin/${editingCode.id}`, payload);
      } else {
        await axios.post(`${BACKEND_URL}/api/promo-codes/admin/create`, payload);
      }
      
      setShowCreateModal(false);
      setEditingCode(null);
      resetForm();
      fetchPromoCodes();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...generateData,
        discount_value: parseFloat(generateData.discount_value),
        count: parseInt(generateData.count),
        max_uses: generateData.max_uses ? parseInt(generateData.max_uses) : null,
        min_order_amount: generateData.min_order_amount ? parseFloat(generateData.min_order_amount) : null,
        end_date: generateData.end_date || null
      };

      await axios.post(`${BACKEND_URL}/api/promo-codes/admin/generate`, payload);
      setShowGenerateModal(false);
      fetchPromoCodes();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de la génération');
    }
  };

  const handleToggle = async (codeId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/promo-codes/admin/${codeId}/toggle`);
      fetchPromoCodes();
    } catch (err) {
      alert('Erreur lors du changement de statut');
    }
  };

  const handleDelete = async (codeId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce code promo ?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/promo-codes/admin/${codeId}`);
      fetchPromoCodes();
    } catch (err) {
      alert('Erreur lors de la suppression');
    }
  };

  const handleEdit = (code) => {
    setEditingCode(code);
    setFormData({
      code: code.code,
      description: code.description || '',
      discount_type: code.discount_type,
      discount_value: code.discount_value,
      start_date: code.start_date ? code.start_date.split('T')[0] : '',
      end_date: code.end_date ? code.end_date.split('T')[0] : '',
      max_uses: code.max_uses || '',
      max_uses_per_customer: code.max_uses_per_customer || '',
      min_order_amount: code.min_order_amount || '',
      max_discount_amount: code.max_discount_amount || '',
      is_active: code.is_active
    });
    setShowCreateModal(true);
  };

  const resetForm = () => {
    setFormData({
      code: '',
      description: '',
      discount_type: 'percentage',
      discount_value: 10,
      start_date: '',
      end_date: '',
      max_uses: '',
      max_uses_per_customer: 1,
      min_order_amount: '',
      max_discount_amount: '',
      is_active: true
    });
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  const filteredCodes = promoCodes.filter(code => 
    code.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (code.description && code.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const exportCodes = () => {
    const csv = [
      ['Code', 'Type', 'Valeur', 'Utilisations', 'Max', 'Actif', 'Expiration'].join(';'),
      ...filteredCodes.map(c => [
        c.code,
        c.discount_type === 'percentage' ? 'Pourcentage' : 'Montant fixe',
        c.discount_type === 'percentage' ? `${c.discount_value}%` : `${c.discount_value}€`,
        c.current_uses,
        c.max_uses || 'Illimité',
        c.is_active ? 'Oui' : 'Non',
        formatDate(c.end_date)
      ].join(';'))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `codes-promo-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin')}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <ArrowLeft size={24} />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Codes Promo</h1>
                <p className="text-sm text-gray-500">{promoCodes.length} codes au total</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={exportCodes}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                <Download size={18} />
                Exporter
              </button>
              <button
                onClick={() => setShowGenerateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition"
              >
                <Sparkles size={18} />
                Générer
              </button>
              <button
                onClick={() => { resetForm(); setEditingCode(null); setShowCreateModal(true); }}
                className="flex items-center gap-2 px-4 py-2 bg-celadon text-white rounded-lg hover:bg-celadon-dark transition"
              >
                <Plus size={18} />
                Nouveau code
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Rechercher un code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-celadon"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-celadon" size={32} />
          </div>
        ) : error ? (
          <div className="text-center py-12 text-red-600">{error}</div>
        ) : filteredCodes.length === 0 ? (
          <div className="text-center py-12">
            <Tag className="mx-auto text-gray-300 mb-4" size={64} />
            <p className="text-gray-500">Aucun code promo trouvé</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Réduction</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Utilisations</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Validité</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredCodes.map((code) => (
                  <tr key={code.id} className={!code.is_active ? 'bg-gray-50 opacity-60' : ''}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-gray-900">{code.code}</span>
                        <button
                          onClick={() => copyToClipboard(code.code)}
                          className="text-gray-400 hover:text-gray-600 transition"
                          title="Copier"
                        >
                          {copiedCode === code.code ? (
                            <CheckCircle size={16} className="text-green-500" />
                          ) : (
                            <Copy size={16} />
                          )}
                        </button>
                      </div>
                      {code.description && (
                        <p className="text-sm text-gray-500 mt-1">{code.description}</p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {code.discount_type === 'percentage' ? (
                          <>
                            <Percent size={16} className="text-purple-500" />
                            <span className="font-medium">-{code.discount_value}%</span>
                          </>
                        ) : (
                          <>
                            <DollarSign size={16} className="text-green-500" />
                            <span className="font-medium">-{code.discount_value}€</span>
                          </>
                        )}
                      </div>
                      {code.min_order_amount && (
                        <p className="text-xs text-gray-500 mt-1">Min. {code.min_order_amount}€</p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Users size={16} className="text-gray-400" />
                        <span>
                          {code.current_uses}
                          {code.max_uses ? ` / ${code.max_uses}` : ' (illimité)'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Calendar size={16} className="text-gray-400" />
                        <span className="text-sm">
                          {code.end_date ? formatDate(code.end_date) : 'Pas d\'expiration'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleToggle(code.id)}
                        className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium transition ${
                          code.is_active 
                            ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {code.is_active ? (
                          <>
                            <ToggleRight size={16} />
                            Actif
                          </>
                        ) : (
                          <>
                            <ToggleLeft size={16} />
                            Inactif
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(code)}
                          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                          title="Modifier"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(code.id)}
                          className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                          title="Supprimer"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">
                {editingCode ? 'Modifier le code promo' : 'Nouveau code promo'}
              </h2>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono uppercase"
                  placeholder="BIENVENUE10"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Code de bienvenue pour les nouveaux clients"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type de réduction *</label>
                  <select
                    value={formData.discount_type}
                    onChange={(e) => setFormData({...formData, discount_type: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="percentage">Pourcentage (%)</option>
                    <option value="fixed">Montant fixe (€)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valeur * {formData.discount_type === 'percentage' ? '(%)' : '(€)'}
                  </label>
                  <input
                    type="number"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({...formData, discount_value: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    min="0"
                    max={formData.discount_type === 'percentage' ? '100' : undefined}
                    step="0.01"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date de début</label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date d'expiration</label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Utilisations max (total)</label>
                  <input
                    type="number"
                    value={formData.max_uses}
                    onChange={(e) => setFormData({...formData, max_uses: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    min="1"
                    placeholder="Illimité"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max par client</label>
                  <input
                    type="number"
                    value={formData.max_uses_per_customer}
                    onChange={(e) => setFormData({...formData, max_uses_per_customer: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    min="1"
                    placeholder="1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Commande min. (€)</label>
                  <input
                    type="number"
                    value={formData.min_order_amount}
                    onChange={(e) => setFormData({...formData, min_order_amount: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    min="0"
                    step="0.01"
                    placeholder="Pas de minimum"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Réduction max. (€)</label>
                  <input
                    type="number"
                    value={formData.max_discount_amount}
                    onChange={(e) => setFormData({...formData, max_discount_amount: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    min="0"
                    step="0.01"
                    placeholder="Pas de plafond"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="w-4 h-4 text-celadon rounded"
                />
                <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                  Code actif
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); setEditingCode(null); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-celadon text-white rounded-lg hover:bg-celadon-dark transition"
                >
                  {editingCode ? 'Enregistrer' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Generate Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Sparkles className="text-purple-500" size={24} />
                Générer des codes
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Créer plusieurs codes promo automatiquement
              </p>
            </div>
            <form onSubmit={handleGenerate} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Préfixe</label>
                  <input
                    type="text"
                    value={generateData.prefix}
                    onChange={(e) => setGenerateData({...generateData, prefix: e.target.value.toUpperCase()})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono uppercase"
                    placeholder="PROMO"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de codes</label>
                  <input
                    type="number"
                    value={generateData.count}
                    onChange={(e) => setGenerateData({...generateData, count: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    min="1"
                    max="100"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={generateData.discount_type}
                    onChange={(e) => setGenerateData({...generateData, discount_type: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="percentage">Pourcentage</option>
                    <option value="fixed">Montant fixe</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valeur</label>
                  <input
                    type="number"
                    value={generateData.discount_value}
                    onChange={(e) => setGenerateData({...generateData, discount_value: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    min="0"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expiration</label>
                  <input
                    type="date"
                    value={generateData.end_date}
                    onChange={(e) => setGenerateData({...generateData, end_date: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Utilisation max</label>
                  <input
                    type="number"
                    value={generateData.max_uses}
                    onChange={(e) => setGenerateData({...generateData, max_uses: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    min="1"
                    placeholder="1"
                  />
                </div>
              </div>

              <div className="bg-purple-50 p-3 rounded-lg">
                <p className="text-sm text-purple-700">
                  <strong>Aperçu :</strong> {generateData.count} codes de type{' '}
                  <strong>{generateData.prefix}-XXXXXX</strong> avec{' '}
                  <strong>
                    {generateData.discount_type === 'percentage' 
                      ? `-${generateData.discount_value}%` 
                      : `-${generateData.discount_value}€`}
                  </strong>
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowGenerateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center gap-2"
                >
                  <Sparkles size={18} />
                  Générer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPromoCodes;
